/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useEffect } from 'react'
import {
  Flex,
  FormControl,
  FormErrorMessage,
  Radio,
  RadioGroup,
  Stack,
  useBreakpointValue
} from '@chakra-ui/react'
import InputText from '@/components/forms/InputText'
import { Controller } from 'react-hook-form'
import FormSection from '../../../FormSection'
import { useResponsive } from '@/hooks/useResponsive'

const CertifyAuthorized = ({
  control,
  currentCreators,
  errors,
  register,
  userNickname,
  setValue,
  watch,
  hasCreators
}) => {
  const attestationType = watch('attestationType')

  useEffect(() => {
    if (attestationType === 'AUTHORIZED_AGENT' && currentCreators) {
      setValue('authorizedAgent', currentCreators[0], { shouldValidate: true })
    }
  }, [attestationType, currentCreators, setValue])

  const CurrentRadio = ({ value, label, disabled }) => {
    return (
      <div >
        <Radio
          value={value}
          isDisabled={disabled}
          cursor={disabled ? 'not-allowed' : 'pointer'}
          opacity={disabled ? 0.6 : 1}
          _disabled={{
            cursor: 'not-allowed',
            opacity: 0.6,
            pointerEvents: 'none',
            borderColor: 'gray.300',
            _before: {
              bg: 'transparent'
            }
          }}
        >
          { label }
        </Radio>
      </div>
    )
  }
  const errorMessage = errors?.attestationType?.message
  const { flexDirection, flexGap } = useResponsive()
  const alignItems = useBreakpointValue({
    base: '',
    md: 'center'
  })

  return (
    <FormSection>
      <p className="has-text-justified mb-2">I, { userNickname }, certify that I am authorized to assert the following claims as:</p>
      <Controller
        name="attestationType"
        control={control}
        render={({ field }) => (
          <FormControl
            isInvalid={errorMessage}
          >
            <RadioGroup {...field}>
              <Stack direction="column">
                <CurrentRadio
                  value="CREATOR"
                  label="Creator"
                />
                <CurrentRadio
                  value="OTHER_COPYRIGHT_CLAIMANT"
                  label="Other copyright claimant"
                />
                <CurrentRadio
                  value="OWNER_OF_EXCLUSIVE_RIGHTS"
                  label="Owner of exclusive right(s)"
                />
              </Stack>
              <Flex
                flexDirection={flexDirection}
                gap={flexGap}
                alignItems={alignItems}
              >
                <div>
                  <CurrentRadio
                    label="Authorized agent of"
                    className="mt-5"
                    value="AUTHORIZED_AGENT"
                  />
                </div>
                <div>
                  <div className="text-sm">
                    Copyright claimant or owner of exclusive right(s)
                  </div>
                  <InputText
                    label=""
                    type="text"
                    errorMessage={errors?.authorizedAgent?.message}
                    { ...register('authorizedAgent') }
                  />
                </div>
              </Flex>
              <p className="mt-5 has-text-justified">
                of the asset registered as this data asset,
                and that the information provided in the asset registration is correct to the best of my knowledge.
              </p>
              <FormErrorMessage>{errorMessage}</FormErrorMessage>
            </RadioGroup>
          </FormControl>
        )}
      />
    </FormSection>
  )
}

export default CertifyAuthorized
